import { z } from 'zod';
import {
  $getFiltersRequestType,
  $getPaginationResponseType,
  $SelectParam,
} from '@shared/types';

export const $SkillsForCreate = z.object({
  existing: z.array(z.number()),
  new: z.array(z.string()),
});
export type SkillsForCreate = z.infer<typeof $SkillsForCreate>;

export const $ResponsibleUser = z.object({
  createdAt: z.string(),
  email: z.string(),
  id: z.number(),
  fullName: z.string(),
  profile: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable(),
    position: z.string().nullable(),
    fullName: z.string(),
  }),
  role: z.object({
    id: z.number(),
    name: z.string(),
    displayName: z.string(),
  }),
});
export type ResponsibleUser = z.infer<typeof $ResponsibleUser>;

export type VacancyActivity = z.infer<typeof $VacancyActivity>;
export const $VacancyActivity = z.object({
  id: z.number(),
  vacancyId: z.number(),
  activeFrom: z.string(),
  activeTo: z.string(),
});

export const $VacancyStatusesHistory = z.object({
  createdAt: z.string(),
  id: z.number(),
  newStatus: z.string(),
  oldStatus: z.string(),
  updatedAt: z.string(),
  user: $ResponsibleUser.nullable(),
});
export type VacancyStatusesHistory = z.infer<typeof $VacancyStatusesHistory>;

const $CandidatePhoto = z.object({
  id: z.number(),
  name: z.string(),
  extension: z.string(),
  size: z.number(),
  absolutePath: z.string(),
});

export const $Candidate = z.object({
  id: z.number(),
  phone: z.string(),
  status: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  age: z.number(),
  salary: z.number(),
  candidateVacancyMatchPercent: z.number(),
  candidateVacancyStatus: z.string(),
  candidateVacancyCreatedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  region: z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    countryCode: z.string(),
  }),
  currency: z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    countryCode: z.string(),
  }),
  paymentPeriod: z.object({
    id: z.number(),
    name: z.string(),
  }),
  employmentTypes: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      slug: z.string(),
    })
  ),
  scheduleWorks: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      slug: z.string(),
    })
  ),
  skills: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      slug: z.string(),
    })
  ),
  activitySpheres: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      slug: z.string(),
      code: z.string().optional(),
    })
  ),
  decisionType: z
    .enum(['WAITING', 'DECLINED', 'POSITIVE', 'NEGATIVE', 'PENDING'])
    .optional(),
  photo: $CandidatePhoto.nullable().optional(),
});
export type Candidate = z.infer<typeof $Candidate>;

const $VacancyCandidateDecisionTypeFilter = z.enum([
  'WAITING',
  'DECLINED',
  'POSITIVE',
  'NEGATIVE',
  'PENDING',
]);

/** GET /api/vacancies/{id}/candidates */
export const $GetVacancyCandidatesRequest = $getFiltersRequestType(
  z.object({
    id: z.union([z.number(), $SelectParam]).optional(),
    phone: z.string().optional(),
    status: z.union([z.string(), $SelectParam]).optional(),
    fullName: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    age: z.number().optional(),
    salary: z.number().optional(),
    experience: z.string().optional(),
    interviewRequired: z.boolean().optional(),
    desiredJob: z.string().optional(),
    employmentDate: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    decisionType: z
      .union([$VacancyCandidateDecisionTypeFilter, $SelectParam])
      .optional(),
    'cities.id': z.number().optional(),
    'cities.name': z.string().optional(),
    'currency.id': z.number().optional(),
    'currency.code': z.string().optional(),
    'currency.name': z.string().optional(),
    'currency.value': z.string().optional(),
    'paymentPeriod.id': z.number().optional(),
    'paymentPeriod.name': z.string().optional(),
  })
);
export type GetVacancyCandidatesRequest = z.infer<
  typeof $GetVacancyCandidatesRequest
>;

/** TariffTransformer — GET /api/vacancies/tariffs */
export type VacancyTariff = z.infer<typeof $VacancyTariff>;
export const $VacancyTariff = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  price: z.number(),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
});

export type Vacancy = z.infer<typeof $Vacancy>;
export const $Vacancy = z.object({
  id: z.number(),
  name: z.string(),
  companyId: z.number(),
  cityIds: z.array(z.number()).optional(),
  cityId: z.number().optional(),
  responsibleUserId: z.number(),
  currencyId: z.number(),
  interviewFinalMessage: z.string().nullable().optional(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  isActive: z.boolean(),
  isArchived: z.boolean(),
  vacancyActivities: z.array($VacancyActivity),
  createdAt: z.string(),
  updatedAt: z.string(),
  requiredCandidatesCount: z.number(),
  status: z.string(),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
  company: z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
    nif: z.string(),
    /** @deprecated Используйте cityId */
    regionId: z.number().optional(),
    cityId: z.number(),
    description: z.string(),
    isActive: z.boolean(),
    isArchived: z.boolean(),
    /** @deprecated Используйте city */
    region: z
      .object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      })
      .optional(),
    city: z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    }),
  }),
  cities: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      })
    )
    .optional(),
  city: z
    .object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    })
    .optional(),
  responsibleUser: $ResponsibleUser,
  employmentTypes: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  scheduleWorks: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  skills: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  activitySpheres: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      slug: z.string(),
      code: z.string().optional(),
    })
  ),
  ai_sphere_fill: z.boolean(),
  paymentPeriod: z.object({
    id: z.number(),
    name: z.string(),
  }),
  statusesHistory: z.array($VacancyStatusesHistory),
  candidates: z.array($Candidate),
  tariffs: $VacancyTariff,
});

export type Vacancies = z.infer<typeof $Vacancies>;
export const $Vacancies = z.array($Vacancy);

/** GET /api/candidates/{id}/vacancies */
export const $CandidateProposedVacancy = z.object({
  id: z.number(),
  name: z.string(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  requiredCandidatesCount: z.number(),
  status: z.string(),
  interviewFinalMessage: z.string().nullable(),
  ai_sphere_fill: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  candidateVacancyStatus: z.string(),
  candidateVacancyMatchPercent: z.number(),
  candidateVacancyCreatedAt: z.string(),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
  city: z.object({
    id: z.number(),
    name: z.string(),
  }),
  responsibleUser: z.object({
    id: z.number(),
    email: z.string(),
    isActive: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    role: z.object({
      id: z.number(),
      name: z.string(),
      displayName: z.string(),
    }),
    profile: z.object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string(),
      position: z.string(),
    }),
  }),
  paymentPeriod: z.object({
    id: z.number(),
    name: z.string(),
  }),
});
export type CandidateProposedVacancy = z.infer<
  typeof $CandidateProposedVacancy
>;
export const $CandidateProposedVacancies = z.array($CandidateProposedVacancy);
export type CandidateProposedVacancies = z.infer<
  typeof $CandidateProposedVacancies
>;

export type GetVacanciesRequest = z.infer<typeof $GetVacanciesRequest>;
export const $GetVacanciesRequest = $getFiltersRequestType(z.object({}));

// Город создаётся тем же контрактом, что и прежний Region
export const $CityForCreate = z.union([
  z.object({
    existing: z
      .object({
        id: z.number().optional(),
      })
      .optional(),
    new: z
      .object({
        name: z.string().optional(),
      })
      .optional(),
  }),
  z
    .object({
      existing: z.array(z.number()).optional(),
      new: z.array(z.string()).optional(),
    })
    .optional(),
]);
export type CityForCreate = z.infer<typeof $CityForCreate>;

/** @deprecated Use $CityForCreate */
export const $RegionForCreate = $CityForCreate;
/** @deprecated Use CityForCreate */
export type RegionForCreate = CityForCreate;

export const $CreateVacancyRequest = z.object({
  name: z.string(),
  companyId: z.number(),
  cityIds: z.array(z.number()),
  responsibleUserId: z.number(),
  currencyId: z.number(),
  tariffId: z.number(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  employmentTypeIds: z.array(z.number()),
  paymentPeriodId: z.number(),
  scheduleWorkIds: z.array(z.number()),
  skills: $SkillsForCreate,
  activitySphereIds: z.array(z.number()),
  ai_sphere_fill: z.boolean(),
  activeFrom: z.string(),
  activeTo: z.string(),
  requiredCandidatesCount: z.number(),
});
export type CreateVacancyRequest = z.infer<typeof $CreateVacancyRequest>;

export const $CreateVacancyResponse = z.object({
  id: z.number(),
  name: z.string(),
  companyId: z.number(),
  cityIds: z.array(z.number()).optional(),
  cityId: z.number().optional(),
  responsibleUserId: z.number(),
  currencyId: z.number(),
  description: z.string(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  isActive: z.boolean(),
  isArchived: z.boolean(),
  vacancyActivities: z.object({
    id: z.number(),
    vacancyId: z.number(),
    activeFrom: z.string(),
    activeTo: z.string(),
  }),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
  company: z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
    nif: z.string(),
    /** @deprecated Используйте cityId */
    regionId: z.number().optional(),
    cityId: z.number(),
    description: z.string(),
    isActive: z.boolean(),
    isArchived: z.boolean(),
    /** @deprecated Используйте city */
    region: z
      .object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      })
      .optional(),
    city: z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    }),
  }),
  cities: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      })
    )
    .optional(),
  city: z
    .object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    })
    .optional(),
  user: z.object({
    id: z.number(),
    email: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  employmentTypes: z.object({
    id: z.number(),
    name: z.string(),
  }),
  scheduleWorks: z.object({
    id: z.number(),
    name: z.string(),
  }),
  skills: z.object({
    id: z.array(z.number()),
    name: z.array(z.string()),
  }),
  paymentPeriods: z.object({
    id: z.number(),
    name: z.string(),
  }),
});
export type CreateVacancyResponse = z.infer<typeof $CreateVacancyResponse>;

export type GetVacancyRequest = z.infer<typeof $GetVacancyRequest>;
export const $GetVacancyRequest = z.object({
  id: z.number(),
});

export const $EditVacancyRequest = z.object({
  name: z.string(),
  companyId: z.number(),
  cityIds: z.array(z.number()),
  currencyId: z.number(),
  tariffId: z.number(),
  interviewFinalMessage: z.string().nullable().optional(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  employmentTypeIds: z.array(z.number()),
  paymentPeriodId: z.number(),
  scheduleWorkIds: z.array(z.number()),
  skills: $SkillsForCreate.nullable(),
  activitySphereIds: z.array(z.number()),
  ai_sphere_fill: z.boolean(),
  requiredCandidatesCount: z.number(),
  status: z.string(),
});
export type EditVacancyRequest = z.infer<typeof $EditVacancyRequest>;

export const $UpdateVacancyResponsibleRequest = z.object({
  responsibleUserId: z.number(),
});
export type UpdateVacancyResponsibleRequest = z.infer<
  typeof $UpdateVacancyResponsibleRequest
>;

export type VacancyStatus = z.infer<typeof $VacancyStatus>;
export const $VacancyStatus = z.object({
  name: z.string(),
  displayName: z.string(),
});

export const $GetVacancyStatusesHistoryRequest = $getFiltersRequestType(
  z.object({})
);
export type GetVacancyStatusesHistoryRequest = z.infer<
  typeof $GetVacancyStatusesHistoryRequest
>;

export const $UpdateVacancyStatusRequest = z.object({
  status: z.string(),
  activeFrom: z.string().optional(),
  activeTo: z.string().optional(),
});
export type UpdateVacancyStatusRequest = z.infer<
  typeof $UpdateVacancyStatusRequest
>;

/** TariffChangeLogTransformer — GET /api/vacancies/tariffs-history */
export const $VacancyTariffChangeLog = z.object({
  id: z.number(),
  tariff_id: z.number(),
  tariff_name: z.string(),
  activation_at: z.string(),
});
export type VacancyTariffChangeLog = z.infer<typeof $VacancyTariffChangeLog>;

export const $VacancyTemplate = z.object({
  id: z.number(),
  name: z.string(),
  author: z.object({
    id: z.number(),
    email: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    vacanciesCount: z.number(),
    role: z.object({
      id: z.number(),
      name: z.string(),
      displayName: z.string(),
      permissions: z.array(z.string()),
    }),
    profile: z.object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string(),
      position: z.string(),
    }),
    company: z.object({
      id: z.number(),
      name: z.string(),
      type: z.string(),
      nif: z.string(),
      /** @deprecated Используйте cityId */
      regionId: z.number().optional(),
      cityId: z.number(),
      description: z.string(),
      isActive: z.boolean(),
      createdAt: z.string(),
      updatedAt: z.string(),
      manager: z.string(),
      /** @deprecated Используйте city */
      region: z
        .object({
          id: z.number(),
          name: z.string(),
          code: z.string(),
          countryCode: z.string(),
        })
        .optional(),
      city: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      }),
      activitySpheres: z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
        code: z.string().optional(),
      }),
    }),
    vacancies: z.object({
      id: z.number(),
      name: z.string(),
      description: z.string(),
      salaryFrom: z.number(),
      salaryTo: z.number(),
      mandatoryRequirements: z.string(),
      additionalRequirements: z.string(),
      responsibilities: z.string(),
      requiredCandidatesCount: z.number(),
      status: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      vacancyActivities: z.object({
        id: z.number(),
        vacancyId: z.number(),
        activeFrom: z.string(),
        activeTo: z.string(),
      }),
      currency: z.object({
        id: z.number(),
        code: z.string(),
        name: z.string(),
        value: z.string(),
      }),
      company: z.object({
        id: z.number(),
        name: z.string(),
        type: z.string(),
        nif: z.string(),
        regionId: z.number(),
        description: z.string(),
        isActive: z.boolean(),
        createdAt: z.string(),
        updatedAt: z.string(),
        manager: z.string(),
        region: z.object({
          id: z.number(),
          name: z.string(),
          code: z.string(),
          countryCode: z.string(),
        }),
        activitySpheres: z.object({
          id: z.number(),
          name: z.string(),
          slug: z.string(),
          code: z.string().optional(),
        }),
      }),
      /** @deprecated Используйте city */
      region: z
        .object({
          id: z.number(),
          name: z.string(),
          code: z.string(),
          countryCode: z.string(),
        })
        .optional(),
      city: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      }),
      responsibleUser: z.string(),
      employmentTypes: z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
      }),
      scheduleWorks: z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
      }),
      skills: z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
      }),
      paymentPeriod: z.object({
        id: z.number(),
        name: z.string(),
      }),
    }),
  }),
  isActive: z.boolean(),
});
export type VacancyTemplate = z.infer<typeof $VacancyTemplate>;

export const $GetVacanciesTemplatesRequest = $getFiltersRequestType(
  z.object({})
);
export type GetVacanciesTemplatesRequest = z.infer<
  typeof $GetVacanciesTemplatesRequest
>;

export const $GetVacanciesTemplatesResponse =
  $getPaginationResponseType($VacancyTemplate);
export type GetVacanciesTemplatesResponse = z.infer<
  typeof $GetVacanciesTemplatesResponse
>;

export const $CreateVacanciesTemplateRequest = z.object({
  name: z.string(),
  currencyId: z.number(),
  paymentPeriodId: z.number(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  employmentTypeIds: z.array(z.number()),
  scheduleWorkIds: z.array(z.number()),
  skills: $SkillsForCreate,
});
export type CreateVacanciesTemplateRequest = z.infer<
  typeof $CreateVacanciesTemplateRequest
>;

export const $DetailVacanciesTemplate = z.object({
  id: z.number(),
  name: z.string(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
  employmentTypes: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  scheduleWorks: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  skills: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  paymentPeriod: z.object({
    id: z.number(),
    name: z.string(),
  }),
  isActive: z.boolean(),
});
export type DetailVacanciesTemplate = z.infer<typeof $DetailVacanciesTemplate>;

export const $UpdateVacanciesTemplateRequest = z.object({
  name: z.string(),
  currencyId: z.number(),
  paymentPeriodId: z.number(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  employmentTypeIds: z.array(z.number()).optional(),
  scheduleWorkIds: z.array(z.number()).optional(),
  skills: $SkillsForCreate,
  isActive: z.boolean(),
});
export type UpdateVacanciesTemplateRequest = z.infer<
  typeof $UpdateVacanciesTemplateRequest
>;

export const $DeleteVacancyTemplateParams = z.object({
  ids: z.array(z.number()),
});
export type DeleteVacancyTemplateParams = z.infer<
  typeof $DeleteVacancyTemplateParams
>;

export const $UpdateVacancyTemplateActivityRequest = z.object({
  isActive: z.boolean(),
});

export type UpdateVacancyTemplateActivityRequest = z.infer<
  typeof $UpdateVacancyTemplateActivityRequest
>;

const $IdNameSlug = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  code: z.string().optional(),
});

export const $CreateVacancyCopyResponse = $Vacancy
  .pick({
    id: true,
    name: true,
    salaryFrom: true,
    salaryTo: true,
    mandatoryRequirements: true,
    additionalRequirements: true,
    responsibilities: true,
    requiredCandidatesCount: true,
    status: true,
    interviewFinalMessage: true,
    ai_sphere_fill: true,
    createdAt: true,
    updatedAt: true,
    currency: true,
    vacancyActivities: true,
    paymentPeriod: true,
    tariffs: true,
    cities: true,
    city: true,
  })
  .extend({
    company: z.object({ id: z.number(), name: z.string() }),
    responsibleUser: z.object({ id: z.number() }),
    activitySpheres: z.array($IdNameSlug),
    employmentTypes: z.array($IdNameSlug),
    scheduleWorks: z.array($IdNameSlug),
    skills: z.array($IdNameSlug),
  });

export type CreateVacancyCopyResponse = z.infer<
  typeof $CreateVacancyCopyResponse
>;

export const $GenerateVacancyRequest = z.object({
  vacancyName: z.string(),
  generationRequest: z.string(),
});
export type GenerateVacancyRequest = z.infer<typeof $GenerateVacancyRequest>;

export const $GenerateVacancyResponse = z.object({
  activitySpheres: z.array($IdNameSlug),
  responsibilities: z.string(),
  requiredCandidatesCount: z.number(),
  city: z.object({
    id: z.number(),
    name: z.string(),
    region: z.object({
      id: z.number(),
      name: z.string(),
    }),
  }),
  employmentTypes: z.array($IdNameSlug),
  scheduleWorks: z.array($IdNameSlug),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
  paymentPeriod: z.object({
    id: z.number(),
    name: z.string(),
  }),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  vacancyActivities: z.array(
    z.object({
      activeFrom: z.string(),
      activeTo: z.string(),
    })
  ),
  skills: z.array($IdNameSlug),
  interviewFinalMessage: z.string(),
  name: z.string(),
});

export type GenerateVacancyResponse = z.infer<typeof $GenerateVacancyResponse>;
